import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  HEADER_ICON_BUTTON,
  MENU_CONTENT,
  MENU_ITEM_BASE,
  MENU_ITEM_DESTRUCTIVE,
} from '@/shared/ui-class';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import { useAIRailScope } from '@/versions/v1/shared/AIRailScopeContext';
import { useChat } from '@/versions/v1/shared/ChatContext';

export function ChatHeader({ layout = 'inline' }: { layout?: 'floating' | 'inline' }) {
  const { scope } = useAIRailScope();
  const { messages, currentTitle, renameThread, deleteCurrentThread } = useChat();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);

  // ai-foundation: header only appears once the thread has content/title.
  if (messages.length === 0) return null;

  const titlePrefix = scope.kind === 'custom' ? `${scope.label} · ` : '';
  const displayTitle = `${titlePrefix}${currentTitle}`;

  const headerClassName =
    layout === 'inline'
      ? 'flex min-w-0 flex-1 items-center justify-between gap-2'
      : 'pointer-events-none absolute left-0 right-0 top-0 z-20 flex h-14 items-center justify-between pl-20 pr-14';

  const titleRowClassName =
    layout === 'inline'
      ? 'flex min-w-0 flex-1 items-center gap-1.5 text-xs'
      : 'pointer-events-auto flex min-w-0 max-w-[20rem] items-center gap-1.5 text-xs md:rounded-md md:bg-[var(--background)]/80 md:px-2 md:py-1 md:backdrop-blur-sm';

  const triggerClassName =
    layout === 'inline'
      ? cn(
          'flex h-auto min-w-0 max-w-full items-center justify-start gap-0.5 rounded-md px-0 py-0 outline-none',
          'text-zinc-500 hover:bg-transparent hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)]',
          'dark:text-zinc-400 dark:hover:bg-transparent dark:hover:text-zinc-200',
        )
      : cn(
          HEADER_ICON_BUTTON,
          'border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] backdrop-blur-md outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40',
          'md:h-auto md:w-auto md:min-w-0 md:max-w-full md:justify-start md:gap-0.5 md:border-0 md:bg-transparent md:backdrop-blur-none',
          'md:text-zinc-500 md:hover:bg-transparent md:hover:text-zinc-700 md:dark:text-zinc-400 md:dark:hover:bg-transparent md:dark:hover:text-zinc-200',
        );

  const titleTextClassName = layout === 'inline' ? 'truncate' : 'hidden truncate md:inline';

  return (
    <header className={headerClassName}>
      <div className={titleRowClassName}>
        {renaming ? (
          <RenameTitleInput
            initial={currentTitle}
            onSubmit={(value) => {
              renameThread(value);
              setRenaming(false);
            }}
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <div className="relative min-w-0 flex-1" ref={menuRef}>
            <button
              type="button"
              className={triggerClassName}
              title={displayTitle}
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Thread options"
            >
              <span className={titleTextClassName}>{displayTitle}</span>
              <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
            </button>
            {menuOpen ? (
              <div
                className={cn(MENU_CONTENT, 'absolute left-0 top-full z-50 mt-1 min-w-[160px]')}
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className={MENU_ITEM_BASE}
                  onClick={() => {
                    setMenuOpen(false);
                    setRenaming(true);
                  }}
                >
                  <Pencil className="h-4 w-4 text-zinc-500" />
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={MENU_ITEM_DESTRUCTIVE}
                  onClick={() => {
                    setMenuOpen(false);
                    deleteCurrentThread();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete chat
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </header>
  );
}

function RenameTitleInput({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (title: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (value.trim().length > 0) onSubmit(value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onBlur={() => {
        if (value.trim().length > 0 && value !== initial) onSubmit(value);
        else onCancel();
      }}
      maxLength={200}
      className="h-6 min-w-0 max-w-full flex-1 rounded-md border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-1.5 text-sm text-zinc-500 outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] dark:border-white/[0.04] dark:bg-[#181818] dark:text-zinc-400"
    />
  );
}
